/**
 * Expanded post card: content, metrics, prediction, panels.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { PlatformBadge } from "@/components/Brand";
import { ChartLine, ArrowsClockwise, Trash, Hash, Link as LinkIcon, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AttributionPanel } from "@/components/posts/AttributionPanel";
import { BitacoraPanel } from "@/components/posts/BitacoraPanel";
import { ChannelsPanel } from "@/components/posts/ChannelsPanel";
import { PredictionBox } from "@/components/posts/PredictionBox";
import { tierFromScore, totalEngagement, withPostTracking } from "@/components/posts/utils";

export function PostCard({ post, product, rank, onChanged, compact = false }) {
  const [open, setOpen] = useState(false);
  const [m, setM] = useState({
    vistas: post.metricas?.vistas || 0,
    likes: post.metricas?.likes || 0,
    comentarios: post.metricas?.comentarios || 0,
    compartidos: post.metricas?.compartidos || 0,
    clicks: post.metricas?.clicks || 0,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/posts/${post.id}/track`, {
        vistas: Number(m.vistas) || 0,
        likes: Number(m.likes) || 0,
        comentarios: Number(m.comentarios) || 0,
        compartidos: Number(m.compartidos) || 0,
        clicks: Number(m.clicks) || 0,
      });
      toast.success("Métricas registradas");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error("Error al registrar");
    } finally {
      setBusy(false);
    }
  };

  const improve = async () => {
    setBusy(true);
    try {
      await api.post(`/posts/${post.id}/improve`);
      toast.success("Publicación mejorada por IA");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al mejorar");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Eliminar publicación?")) return;
    await api.delete(`/posts/${post.id}`);
    onChanged?.();
  };

  const score = post.score || 0;
  const scoreColor = score >= 60 ? "#00C853" : score >= 30 ? "#FFCC00" : "#FF2A00";
  const tier = tierFromScore(score);
  const medal = rank === 1 ? { emoji: "🥇", bg: "#FFC107", label: "Top 1" }
              : rank === 2 ? { emoji: "🥈", bg: "#B0BEC5", label: "Top 2" }
              : rank === 3 ? { emoji: "🥉", bg: "#D7A86E", label: "Top 3" }
              : null;
  const eng = totalEngagement(post);
  const imgs = post.imagenes_base64 || [];
  const heroIdx = Math.min(post.mejor_imagen_idx || 0, Math.max(0, imgs.length - 1));
  const prevHistory = (post.historial || [])[(post.historial || []).length - 1];
  const heroChanged =
    !!prevHistory &&
    typeof prevHistory.mejor_imagen_idx === "number" &&
    prevHistory.mejor_imagen_idx !== heroIdx;

  return (
    <div
      className={compact ? "" : "border border-zinc-200 p-6 relative overflow-hidden"}
      style={compact ? undefined : { borderTop: `4px solid ${tier.color}` }}
      data-testid={`post-card-${post.id}`}
    >
      {!compact && medal && (
        <div
          className="absolute top-0 right-0 px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 z-10 shadow-sm"
          style={{ background: medal.bg, color: "#0B0B0F" }}
          data-testid={`post-medal-${post.id}`}
          title={`${medal.label} por score`}
        >
          <span className="text-base leading-none">{medal.emoji}</span> {medal.label}
        </div>
      )}
      {!compact && imgs.length > 0 && (
        <div className="mb-4 -mx-6 -mt-6">
          <div className="aspect-[16/9] bg-zinc-50 overflow-hidden border-b border-zinc-200 relative">
            <img
              src={`data:image/jpeg;base64,${imgs[heroIdx]}`}
              alt={post.titulo}
              className="w-full h-full object-cover"
            />
            {heroChanged && (
              <span
                className="absolute top-2 left-2 bg-klein text-white text-[10px] uppercase tracking-[0.18em] px-2 py-1"
                data-testid={`hero-changed-${post.id}`}
                title={`IA cambió la portada (antes: foto ${prevHistory.mejor_imagen_idx + 1})`}
              >
                IA cambió portada
              </span>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="px-6 pt-3 flex gap-1 overflow-x-auto">
              {imgs.map((b64, i) => (
                <div
                  key={`${b64?.slice(-16) || "img"}-${i}`}
                  className={`shrink-0 w-10 h-10 border ${
                    i === heroIdx ? "border-klein ring-1 ring-klein" : "border-zinc-200"
                  }`}
                >
                  <img
                    src={`data:image/jpeg;base64,${b64}`}
                    alt={`thumb ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className={compact ? "hidden" : "flex items-start justify-between gap-3"}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <PlatformBadge platform={post.plataforma} />
            <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200">{post.tono}</span>
            <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200">v{post.version}</span>
            <span className="text-muted2">{product?.nombre || "Producto eliminado"}</span>
          </div>
          <div className="h-display text-xl font-bold tracking-tight mt-2">{post.titulo}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="overline">Score</div>
          <div
            className="h-display text-3xl font-black tabular-nums"
            style={{ color: scoreColor }}
          >
            {score}
          </div>
          <div
            className="text-[10px] uppercase tracking-[0.18em] font-medium mt-0.5 px-1.5 py-0.5 inline-block"
            style={{ color: tier.color, background: tier.bg }}
          >
            {tier.label}
          </div>
          {eng > 0 && (
            <div className="text-[10px] text-muted2 mt-1 tabular-nums" title="Interacciones totales ponderadas">
              {eng} eng
            </div>
          )}
        </div>
      </div>

      <p className="text-sm mt-3 leading-relaxed line-clamp-4 whitespace-pre-line">{post.contenido}</p>

      {post.cta && <div className="text-sm mt-2"><span className="overline">CTA</span> {post.cta}</div>}

      {post.hashtags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {post.hashtags.slice(0, 8).map((h) => (
            <span key={h} className="text-[11px] px-1.5 py-0.5 bg-zinc-50 border border-zinc-100 inline-flex items-center gap-0.5">
              <Hash size={10} />
              {h}
            </span>
          ))}
        </div>
      )}

      {post.prediccion && (post.prediccion.score_max > 0 || post.prediccion.score_min > 0) && (
        <PredictionBox p={post.prediccion} actual={post.score} />
      )}

      {post.enlace_producto && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <LinkIcon size={14} className="text-klein shrink-0" />
          <a
            href={withPostTracking(post.enlace_producto, post.id)}
            target="_blank"
            rel="noreferrer"
            className="text-klein hover:underline truncate flex-1 font-mono"
            data-testid={`link-${post.id}`}
            title="Este enlace cuenta clicks automáticamente"
          >
            {withPostTracking(post.enlace_producto, post.id)}
          </a>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(withPostTracking(post.enlace_producto, post.id));
              toast.success("Enlace copiado · este link cuenta clicks");
            }}
            className="p-1.5 hover:bg-zinc-100"
            title="Copiar enlace con tracking"
            data-testid={`copy-link-${post.id}`}
          >
            <Copy size={12} />
          </button>
        </div>
      )}

      <div className="mt-5 grid grid-cols-5 border-t border-l border-zinc-100 text-center">
        <Metric label="Vistas" value={post.metricas?.vistas || 0} />
        <Metric label="Likes" value={post.metricas?.likes || 0} />
        <Metric label="Coment." value={post.metricas?.comentarios || 0} />
        <Metric label="Comp." value={post.metricas?.compartidos || 0} />
        <Metric label="Clicks" value={post.metricas?.clicks || 0} />
      </div>

      <AttributionPanel postId={post.id} />

      <ChannelsPanel post={post} onChanged={onChanged} />

      <BitacoraPanel postId={post.id} />

      <div className="flex flex-wrap gap-2 mt-4">
        <button className="btn-ghost" onClick={() => setOpen(!open)} data-testid={`track-${post.id}`}>
          <ChartLine size={16} /> Registrar métricas
        </button>
        <button className="btn-ghost" onClick={improve} disabled={busy} data-testid={`improve-${post.id}`}>
          <ArrowsClockwise size={16} /> Mejorar con IA
        </button>
        <button className="btn-ghost text-[#FF2A00]" onClick={onDelete} data-testid={`delete-post-${post.id}`}>
          <Trash size={16} />
        </button>
      </div>

      {open && (
        <div className="mt-4 p-4 bg-zinc-50 border border-zinc-200 grid grid-cols-2 md:grid-cols-5 gap-3">
          {["vistas", "likes", "comentarios", "compartidos", "clicks"].map((k) => (
            <div key={k}>
              <label className="overline block mb-1">{k}</label>
              <input
                type="number"
                min="0"
                className="input-flat"
                value={m[k]}
                onChange={(e) => setM({ ...m, [k]: e.target.value })}
                data-testid={`m-${k}-${post.id}`}
              />
            </div>
          ))}
          <div className="col-span-2 md:col-span-5 flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
            <button className="btn-klein" onClick={save} disabled={busy} data-testid={`save-metrics-${post.id}`}>
              Guardar métricas
            </button>
          </div>
        </div>
      )}

      {post.historial?.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs text-muted2 cursor-pointer hover:text-ink">
            Historial de versiones ({post.historial.length})
          </summary>
          <ul className="mt-2 space-y-2">
            {post.historial.map((h) => (
              <li key={`v${h.version}`} className="text-xs border-l-2 border-zinc-200 pl-3 py-1">
                <span className="overline">v{h.version}</span> · score{" "}
                <span style={{ color: h.score >= 60 ? "#00C853" : h.score >= 30 ? "#FFCC00" : "#FF2A00" }}>
                  {h.score}
                </span>{" "}
                · {h.titulo}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


function Metric({ label, value }) {
  return (
    <div className="px-2 py-2 border-r border-b border-zinc-100">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted2">{label}</div>
      <div className="text-sm font-mono font-medium tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
